import React, { useEffect } from "react";
import { Alert, Box, Button, Text } from "@showitinc/showit-wc";

import Modal from "../base/Modal";
import { useSiteState } from "../../hooks/query/useSite";
import useRendererLock from "../../hooks/query/useRendererLock.mjs";
import UpgradedIcon from "../../components/UpgradedIcon";
import styles from "./RendererLockInModal.module.css";

/**
 * Confirmation modal for the one-way v2 renderer lock. Mounted at the
 * DesignView level via DesignViewModals so it survives the Site Settings
 * tab flip that fires from `SiteActions.designLocked` (dispatched from
 * `useRendererLock`'s onSuccess). Transitions from the acknowledgement view
 * to the "Upgrade Complete" view in place after the lock resolves; the
 * customer dismisses it from the success view.
 *
 * @param {Object} props
 * @param {boolean} [props.show]
 * @param {() => void} [props.onHide]
 * @returns {React.ReactElement}
 */
const RendererLockInModal = ({ show, onHide }) => {
  const siteId = useSiteState().get("site_design_id");
  const { lock, isLocking, lockResult, lockCompleted, lockError, resetLock } =
    useRendererLock(siteId, { enabled: false });

  const isComplete = Boolean(lockCompleted && lockResult?.locked);

  useEffect(() => {
    if (show) {
      resetLock();
    }
  }, [show, resetLock]);

  if (isComplete) {
    return (
      <Modal
        title="Upgrade"
        className={styles.dialog}
        isOpen={!!show}
        onClose={onHide}
        primaryButtonTitle="Close"
        primaryButtonAction={onHide}
      >
        <div className={styles.successBody}>
          <UpgradedIcon className={styles.icon} />
          <Text variant="heading-1" tag="p" className={styles.heading}>
            Upgrade Complete
          </Text>
          <Text variant="body" tag="p" className={styles["paragraph-large"]}>
            This design has gained access to all the latest features of the
            Showit Builder.
          </Text>
          <Text variant="body" tag="p" className={styles.paragraph}>
            Should you need to reference a previous version of your site, a copy
            has been saved in your site designs.
          </Text>
        </div>
      </Modal>
    );
  }

  const errorMessage = lockError
    ? lockError?.message || "Something went wrong. Try again."
    : lockCompleted && lockResult && !lockResult.locked
      ? "This design is no longer eligible to lock. Close this and try again."
      : null;

  return (
    <Modal
      title="Upgrade"
      isOpen={!!show}
      onClose={isLocking ? () => {} : onHide}
      closeOnOverlayClick={!isLocking}
      size={null}
      className={styles.dialog}
      secondaryButtonTitle="Cancel"
      secondaryButtonAction={onHide}
      secondaryButtonDisabled={isLocking}
    >
      <div className={styles.ackBody}>
        <UpgradedIcon className={styles.ackIcon} />
        <Text variant="heading-1" tag="p" className={styles.heading}>
          Upgrade to the latest Publish Engine
        </Text>
        <Text variant="body" tag="p" className={styles.paragraph}>
          Unlock the latest Showit features and improvements, plus new
          capabilities as they’re released.
        </Text>
        <Text variant="body" tag="p" className={styles.paragraph}>
          We’ll create a backup copy of your current design first, so you can
          return to the previous version if needed.
        </Text>
        <Button variant="feature" onSIClick={lock} disabled={isLocking}>
          {isLocking ? "Locking…" : "Upgrade"}
        </Button>
        {errorMessage && (
          <Box className={styles.alertWrap}>
            <Alert
              variant="error"
              iconPrefix="warning"
              stacked
              message={errorMessage}
            />
          </Box>
        )}
      </div>
    </Modal>
  );
};

export default RendererLockInModal;
